import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';

import { AppState } from 'src/config/appState';

import { getCPProgramState } from '../../selectors/cpProgramSelectors';
import { Answer, ContentQuestion, OptionsQuestion, SurveyQuestion } from '../models/weekFour';

export const getFlexibilityExerciseData = (state: AppState) =>
    getCPProgramState(state).flexibilityExerciseQuestions;

const getQuestionId = (_state: AppState, id: number) => id;

export const getSelectedQuestionById = createSelector(
    getFlexibilityExerciseData,
    getQuestionId,
    (questions: OptionsQuestion[], questionId) =>
        questions.find(question => question.id === questionId),
);

export const getSelectedAnswerByQuestionId = createSelector(
    getSelectedQuestionById,
    (question: OptionsQuestion): Answer | undefined => {
        const selectedAnswer = question.answers.find(answer => answer.isChosen);
        if (!selectedAnswer) {
            return;
        }

        return selectedAnswer;
    },
);

export const getSelectedAnswerDescription = createSelector(
    getSelectedAnswerByQuestionId,
    selectedAnswer => selectedAnswer && selectedAnswer.description,
);

export const getSelectedAnswerAdditionalAnswer = createSelector(
    getSelectedAnswerByQuestionId,
    selectedAnswer => selectedAnswer && selectedAnswer.additionalAnswer,
);

export const getFlexibilityExerciseSelectedAnswers = createSelector(
    getFlexibilityExerciseData,
    exerciseQuestions =>
        flatten(
            exerciseQuestions.map(question =>
                question.answers
                    .filter(answer => answer.isChosen)
                    .map(selectedAnswer => ({
                        id: question.id,
                        ...selectedAnswer,
                    })),
            ),
        ),
);

export const isFlexibilityExerciseDataValid = createSelector(
    getFlexibilityExerciseData,
    questions =>
        !questions.some(
            question =>
                !question.answers.find(answer =>
                    answer.isChosen
                        ? answer.description === 'Other'
                            ? !isEmpty(answer.additionalAnswer)
                            : !isEmpty(answer.description)
                        : false,
                ),
        ),
);

export const getFlexibilityPracticeQuestion = (state: AppState) =>
    getCPProgramState(state).flexibilityPracticeQuestion;

export const getFlexibilityPracticeAnswer = createSelector(
    getFlexibilityPracticeQuestion,
    question => ({
        id: question.id,
        answer: question.answer,
    }),
);

export const getFlexibilityPlanData = (state: AppState): SurveyQuestion[] =>
    getCPProgramState(state).flexibilityPlan;

export const getFlexibilityPracticeSelectedQuestionById = createSelector(
    getFlexibilityPlanData,
    getQuestionId,
    (questions: OptionsQuestion[], questionId) =>
        questions.find(question => question.id === questionId),
);

export const getPracticeSelectedAnswerByQuestionId = createSelector(
    getFlexibilityPracticeSelectedQuestionById,
    (question: OptionsQuestion): Answer | undefined => {
        const selectedAnswer = question.answers.find(answer => answer.isChosen);

        return selectedAnswer;
    },
);

export const getFlexibilityPlanQuestionContent = createSelector(
    getFlexibilityPlanData,
    (flexibilityPlanQuestion: ContentQuestion[]) =>
        !isEmpty(flexibilityPlanQuestion) && flexibilityPlanQuestion[0],
);

export const getFlexibilityPlanQuestionOption = createSelector(
    getFlexibilityPlanData,
    (flexibilityPlanQuestion: OptionsQuestion[]) =>
        !isEmpty(flexibilityPlanQuestion) && flexibilityPlanQuestion[1],
);

export const isFlexibilityPlanDataValid = createSelector(getFlexibilityPlanData, questions => {
    const isAnswerGiven = questions.find(
        (question: ContentQuestion) => !!question.answer && question.answer !== '',
    );

    const isOptionChosen = !questions.some(
        ({ answers }: OptionsQuestion) =>
            answers &&
            !answers.find(answer =>
                answer.isChosen
                    ? answer.description === 'Other'
                        ? !isEmpty(answer.additionalAnswer)
                        : !isEmpty(answer.description)
                    : false,
            ),
    );

    return isAnswerGiven && isOptionChosen;
});

export const getFlexibilityPlanSelectedAnswers = createSelector(
    getFlexibilityPlanData,
    (questions: SurveyQuestion[]) => {
        const givenAnswer = questions.find((question: ContentQuestion) => question.answer !== '');

        const selectedOption = flatten(
            questions.map(
                ({ answers }: OptionsQuestion) =>
                    answers &&
                    answers
                        .filter(answer => answer && answer.isChosen)
                        .map(option => ({
                            id: questions[1].id,
                            description: option.description,
                            additionalAnswer: option.additionalAnswer,
                        })),
            ),
        );

        return [givenAnswer, selectedOption[1]];
    },
);
